import { PhoneNumberUtil, PhoneNumberType, PhoneNumberFormat } from "google-libphonenumber"
import type { Country } from "@/types/api-types"

const phoneUtil = PhoneNumberUtil.getInstance()

const ISRAELI_FAKE_GOVERNMENTAL_ID = "123789456"
const UAE_FAKE_GOVERNMENTAL_ID_SUFFIX = "12345678"

const isTester = (phoneNumber: string, defaultLocale: string): boolean => {
  return (
    defaultLocale === "IL" &&
    ((phoneNumber.startsWith("05111111") && phoneNumber.length === 10 && /^\d+$/.test(phoneNumber)) ||
      (phoneNumber.startsWith("+9725111111") && phoneNumber.length === 13 && /^\+\d+$/.test(phoneNumber)))
  )
}

export const fixPhone = (
  phoneNumber: string | null | undefined,
  defaultLocale: string | null | undefined,
): string | null => {
  if (!phoneNumber || !defaultLocale) {
    return null
  }
  const phone = phoneUtil.parse(phoneNumber, defaultLocale)
  return phoneUtil.format(phone, PhoneNumberFormat.E164)
}

export const isPhoneValid = (
  phoneNumber: string | null | undefined,
  defaultLocale: string | null | undefined,
): boolean => {
  if (!phoneNumber || !defaultLocale) {
    return false
  }
  try {
    if (isTester(phoneNumber, defaultLocale)) {
      return true
    }
    const phone = phoneUtil.parse(phoneNumber, defaultLocale)
    const countryCode = phone.getCountryCode()
    const isMobile = phoneUtil.getNumberType(phone) === PhoneNumberType.MOBILE
    const isCountryCoded = phoneNumber.startsWith(String(countryCode)) || phoneNumber.startsWith(`+${countryCode}`)
    if (!isMobile && !isCountryCoded) {
      return false
    }
    return phoneUtil.isValidNumber(phone)
  } catch {
    return false
  }
}

const verifyChecksumNumber = (governmentalId: string): boolean => {
  const reverseGovernmentalId = [...governmentalId].reverse().join("")
  let sum = 0
  for (let i = 0; i < reverseGovernmentalId.length; i++) {
    const digit = parseInt(reverseGovernmentalId.charAt(i))
    let idDigitCounter = i % 2 === 0 ? digit : digit * 2
    if (idDigitCounter > 9) {
      idDigitCounter = Math.floor(idDigitCounter / 10) + (idDigitCounter % 10)
    }
    sum += idDigitCounter
  }
  return sum % 10 === 0
}

const validateIsraeliGovernmentalId = (governmentalId: string): boolean => {
  if (!/^\d+$/.test(governmentalId) || governmentalId.length !== 9 || parseInt(governmentalId) === 0) {
    return false
  }
  if (governmentalId === ISRAELI_FAKE_GOVERNMENTAL_ID) {
    return true
  }
  return verifyChecksumNumber(governmentalId)
}

const validateUaeGovernmentalId = (governmentalId: string, birthYear: string): boolean => {
  if (!/^\d+$/.test(governmentalId) || governmentalId.length !== 15 || parseInt(governmentalId) === 0) {
    return false
  }
  if (governmentalId.slice(0, 3) !== "784") {
    return false
  }
  if (governmentalId.slice(3, 7) !== birthYear) {
    return false
  }
  if (governmentalId.slice(7) === UAE_FAKE_GOVERNMENTAL_ID_SUFFIX) {
    return true
  }
  return verifyChecksumNumber(governmentalId)
}

export const validateGovernmentalId = (
  governmentalIdType: Country | null | undefined,
  governmentalId: string | null | undefined,
  birthYear: string,
): boolean => {
  if (!governmentalId) {
    return false
  }
  if (governmentalIdType === "Israel") {
    return validateIsraeliGovernmentalId(governmentalId)
  }
  if (governmentalIdType === "UAE") {
    return validateUaeGovernmentalId(governmentalId, birthYear)
  }
  return true
}
